import axiosInstance from '../auth/axiosInstance';
import { chatApiSubCapabilityId } from '../utils/chatThreadUtils';
import { invalidateAfterChatChange } from '../utils/clientProjectDataCache';

export const fetchChat = async (payload) => {
  try {
    const { data } = await axiosInstance.post('/Chat/get-chat-history', payload);
    return data;
  } catch (error) {
    console.error('Error fetching reports:', error);
    throw error;
  }
};

export const handleSendMessage = async (
  userId,
  capabilityId,
  subCapabilityId,
  projectId,
  message,
  attachment,
  setMessage,
  setFile,
  fetchChatHistory,
  userName
) => {
  const formData = new FormData();
  formData.append('UserId', userId);
  formData.append('ProjectId', projectId);
  formData.append('UserName', userName);
  formData.append('CapabilityId', capabilityId);
  formData.append('SubCapabilityId', String(chatApiSubCapabilityId(subCapabilityId)));
  formData.append('Message', message);
  formData.append('MessageDate', new Date().toISOString());

  if (attachment) {
    formData.append('file', attachment);
  }

  try {
    const { status } = await axiosInstance.post('/Chat/add-capability-chat', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (status === 200) {
      invalidateAfterChatChange(userId, projectId);
      if (typeof setMessage === 'function') {
        setMessage('');
      }
      if (typeof setFile === 'function') {
        setFile(null);
      }

      if (typeof fetchChatHistory === 'function') {
        try {
          await fetchChatHistory();
        } catch (historyError) {
          console.error('Error fetching chat history:', historyError.message);
        }
      }

      return true;
    }

    throw new Error('Failed to send message: Server returned an error.');
  } catch (error) {
    console.error('Error sending message:', error.message);

    if (error.response) {
      throw new Error(
        error.response.data?.message || 'Unknown error from server.'
      );
    }

    if (error.request) {
      throw new Error('Network error occurred.');
    }

    throw error;
  }
};

export const downloadChatFile = async (filename) => {
  try {
    if (!filename || filename.trim() === '') {
      alert('Filename is required.');
      return;
    }

    const { data } = await axiosInstance.get('/Chat/download-chat-file', {
      params: { filename },
      responseType: 'blob',
    });

    const fileBlob = new Blob([data]);
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = URL.createObjectURL(fileBlob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(link.href);
    link.remove();
  } catch (error) {
    console.error('Error downloading the file:', error.message);
    alert('Failed to download the file.');
  }
};

export const getChatCount = async (payload) => {
  try {
    const { data } = await axiosInstance.post('/Chat/get-chat-count', payload);
    return data;
  } catch (error) {
    return null;
  }
};

export const getActiveChatCount = async (payload) => {
  try {
    if (!payload) {
      throw new Error('Project ID is required to fetch assessment details.');
    }

    const { data } = await axiosInstance.post('/Chat/get-active-chat-count', payload);
    return data;
  } catch (error) {
    console.error('Error fetching reports:', error);
    throw error;
  }
};

export const getSmeChatMode = async (payload) => {
  try {
    const { data } = await axiosInstance.post('/Chat/get-sme-chat-mode', payload);
    return { auto: data?.data?.auto === true };
  } catch (error) {
    console.error('Error fetching SME chat mode:', error);
    return { auto: false };
  }
};

const normalizeUnusedBySubCapability = (value) => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  const normalized = {};
  Object.entries(value).forEach(([subCapabilityId, questions]) => {
    if (!Array.isArray(questions)) {
      return;
    }
    const list = questions
      .filter((question) => typeof question === 'string' && question.trim())
      .map((question) => question.trim());
    if (list.length > 0) {
      normalized[String(subCapabilityId)] = list;
    }
  });
  return normalized;
};

export const getSuggestedChatQuestions = async (payload) => {
  try {
    const { data } = await axiosInstance.post('/Chat/get-suggested-questions', payload);
    const body = data?.data;
    if (Array.isArray(body)) {
      return { auto: false, questions: body, unusedBySubCapability: {} };
    }
    const unused =
      body?.unusedBySubCapability ??
      body?.UnusedBySubCapability ??
      {};
    return {
      auto: body?.auto === true,
      questions: Array.isArray(body?.questions) ? body.questions : [],
      unusedBySubCapability: normalizeUnusedBySubCapability(unused),
    };
  } catch (error) {
    console.error('Error fetching suggested chat questions:', error);
    return { auto: false, questions: [], unusedBySubCapability: {} };
  }
};

export const syncSuggesterUnused = async ({
  userId,
  projectId,
  smeUserId,
  subCapabilityId,
  questions,
}) => {
  try {
    await axiosInstance.post('/Chat/sync-suggester-unused', {
      userId,
      projectId,
      smeUserId,
      subCapabilityId: chatApiSubCapabilityId(subCapabilityId),
      suggestions: Array.isArray(questions) ? questions : [],
    });
    return true;
  } catch (error) {
    console.error('Error syncing unused suggester questions:', error);
    return false;
  }
};

export const setSmeChatAuto = async (payload) => {
  try {
    const { data } = await axiosInstance.post('/Chat/set-sme-chat-auto', payload);
    return data?.data?.auto === true;
  } catch (error) {
    console.error('Error saving SME chat auto flag:', error);
    return payload?.auto === true;
  }
};

export const sendInterviewQuestion = async ({ conversationId, content }) => {
  try {
    await axiosInstance.post('/Chat/send-interview-question', {
      conversationId,
      content,
    });
    return true;
  } catch (error) {
    console.error('Error sending interview question:', error);
    return false;
  }
};

export const submitInterviewClientReply = async ({
  conversationId,
  content,
  capabilityId,
  subCapabilityId,
}) => {
  try {
    const { data } = await axiosInstance.post('/Chat/submit-interview-client-reply', {
      conversationId,
      content,
      capabilityId,
      subCapabilityId,
    });
    return data?.data ?? null;
  } catch (error) {
    console.error('Error submitting interview client reply:', error);
    return null;
  }
};

export const refreshInterviewSuggestion = async ({
  conversationId,
  capabilityId,
  subCapabilityId,
}) => {
  try {
    const { data } = await axiosInstance.post('/Chat/refresh-interview-suggestion', {
      conversationId,
      capabilityId,
      subCapabilityId,
    });
    return data?.data ?? null;
  } catch (error) {
    console.error('Error refreshing interview suggestion:', error);
    return null;
  }
};
